import { ApolloClient } from '@apollo/client';
import { get } from 'lodash';
import { QUERY_REGISTRY } from './queryRegistry';
import { SuggestionExecutionResult, SuggestionItem } from './types';

/**
 * Executes the GraphQL query associated with a suggestion,
 * extracts the field at `fieldPath`, and applies the formatter.
 *
 * For NAVIGATE suggestions (no real query), returns the formatter
 * result directly without hitting the network.
 */
export async function executeSuggestion(
  suggestion: SuggestionItem,
  domainIdentifier: string,
  client: ApolloClient<object>,
  timeRangeOverride?: { startTime: number; endTime: number },
): Promise<SuggestionExecutionResult> {
  // NAVIGATE suggestions are UI navigation shortcuts (e.g., "Show me EBS Dashboard").
  // They have no GraphQL query and no entry in QUERY_REGISTRY — they only need the
  // formatter to produce display text. Must be checked before the registry lookup.
  if (suggestion.queryId === 'NAVIGATE') {
    const formatted = suggestion.formatter(null);
    return { success: true, data: formatted };
  }

  const queryConfig = QUERY_REGISTRY[suggestion.queryId];

  if (!queryConfig) {
    return { success: false, data: '', error: `Unknown query: ${suggestion.queryId}` };
  }

  if (queryConfig.gqlQuery === null) {
    const formatted = suggestion.formatter(null);
    return { success: true, data: formatted };
  }

  try {
    let variables = queryConfig.buildVariables(domainIdentifier);

    // CW_METRICS has its own variable construction — handle separately
    if (suggestion.queryId === 'CW_METRICS') {
      variables = buildCwMetricsVariables(suggestion, domainIdentifier, variables, queryConfig, timeRangeOverride);
    } else if (timeRangeOverride) {
      variables = queryConfig.transformTimeRange
        ? { ...variables, ...queryConfig.transformTimeRange(timeRangeOverride) }
        : { ...variables, startTime: timeRangeOverride.startTime, endTime: timeRangeOverride.endTime };
    }

    const { data } = await client.query({
      query: queryConfig.gqlQuery,
      variables,
      fetchPolicy: 'network-only',
    });

    const value = get(data, suggestion.fieldPath, null);
    const formatted = suggestion.formatter(value);

    // Extract chart data for CW_METRICS responses
    let chartData: any = undefined;
    if (suggestion.queryId === 'CW_METRICS' && value && value.timeStamps && value.externalMetrics) {
      chartData = {
        timestamps: value.timeStamps,
        series: Array.isArray(value.externalMetrics) ? value.externalMetrics : [value.externalMetrics],
        title: suggestion.label,
      };
    }

    return { success: true, data: formatted, chartData };
  } catch (err: any) {
    return { success: false, data: '', error: err.message || 'Query failed' };
  }
}

/**
 * Build CW_METRICS-specific variables, resolving the metric name
 * from the dedicated `metricName` field or falling back to the suggestion ID.
 */
function buildCwMetricsVariables(
  suggestion: SuggestionItem,
  domainIdentifier: string,
  baseVariables: Record<string, any>,
  queryConfig: { transformTimeRange?: (o: { startTime: number; endTime: number }) => Record<string, any> },
  timeRangeOverride?: { startTime: number; endTime: number },
): Record<string, any> {
  const cwMetricName = suggestion.metricName || resolveCwMetricName(suggestion.id);

  const [clientId, domainName] = domainIdentifier.split(':');
  const metricsPayload = JSON.stringify([{
    alias: 'm0',
    namespace: 'AWS/ES',
    dimensions: [
      { name: 'DomainName', value: domainName },
      { name: 'ClientId', value: clientId },
    ],
    account_id: clientId,
    metric_name: cwMetricName,
    stats: ['Average', 'Maximum'],
  }]);

  const timeVars = timeRangeOverride && queryConfig.transformTimeRange
    ? queryConfig.transformTimeRange(timeRangeOverride)
    : {
        startTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        endTime: new Date(),
      };

  return { ...baseVariables, metrics: metricsPayload, ...timeVars };
}

function resolveCwMetricName(suggestionId: string): string {
  const metricName = suggestionId.replace('dashboard.', '').replace('cw.', '');
  const metricMap: Record<string, string> = {
    searchRate: 'SearchRate',
    indexingRate: 'IndexingRate',
    searchLatency: 'SearchLatency',
    cpuUtilization: 'CPUUtilization',
    jvmMemoryPressure: 'JVMMemoryPressure',
    clusterUsedSpace: 'ClusterUsedSpace',
    '4xxErrors': '4xx',
    '5xxErrors': '5xx',
  };
  return metricMap[metricName] || metricName;
}
